import { useState, useCallback, useEffect } from 'react';
import { ReportChat } from '../components/financial-summary/ReportChat';
import { ReportWorkspace } from '../components/financial-summary/ReportWorkspace';
import { financialSummaryApi } from '../services/financialSummaryApi';
import { useCompany } from '../contexts/CompanyContext';

export default function FinancialSummaryPage() {
  const { selectedCompanies, addCompany, removeCompany } = useCompany();
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [reportTitle, setReportTitle] = useState('');
  const [reportPages, setReportPages] = useState([]);

  // Saved reports
  const [savedReports, setSavedReports] = useState([]);
  const [activeReportId, setActiveReportId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Load saved reports on mount
  useEffect(() => {
    financialSummaryApi.listReports('admin').then(data => {
      setSavedReports(Array.isArray(data) ? data : (data.reports || []));
    }).catch(console.error);
  }, []);

  const refreshReports = useCallback(async () => {
    try {
      const data = await financialSummaryApi.listReports('admin');
      setSavedReports(Array.isArray(data) ? data : (data.reports || []));
    } catch (e) {
      console.error('Failed to refresh reports:', e);
    }
  }, []);

  const handleReportPage = useCallback((pageData) => {
    if (!pageData?.page?.html) return;
    setReportTitle(pageData.title || reportTitle);
    setReportPages(prev => {
      const existing = prev.findIndex(p => p.order === pageData.page.order);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = pageData.page;
        return updated;
      }
      return [...prev, pageData.page];
    });
  }, [reportTitle]);

  const handleReportComplete = useCallback(() => {
    // Could auto-save here
  }, []);

  const handleNewChat = useCallback(() => {
    setActiveThreadId(null);
    setReportPages([]);
    setReportTitle('');
    setActiveReportId(null);
  }, []);

  // Save report
  const handleSave = useCallback(async () => {
    if (!reportPages.length) return;
    setSaving(true);
    try {
      const title = reportTitle || 'Financial Report';
      const companyNames = selectedCompanies.map(c => c.company_name || c.rf_id);
      if (activeReportId) {
        // Update existing
        await financialSummaryApi.updateReport(activeReportId, {
          title,
          company_names: companyNames,
          pages: reportPages,
          user_id: 'admin',
        });
      } else {
        // Create new
        const id = crypto.randomUUID();
        const result = await financialSummaryApi.saveReport({
          id,
          title,
          company_names: companyNames,
          thread_id: activeThreadId,
          pages: reportPages,
          user_id: 'admin',
        });
        setActiveReportId(result.id || id);
      }
      await refreshReports();
    } catch (e) {
      console.error('Failed to save report:', e);
    } finally {
      setSaving(false);
    }
  }, [reportPages, reportTitle, selectedCompanies, activeReportId, activeThreadId, refreshReports]);

  // Load a saved report
  const handleLoadReport = useCallback(async (reportId) => {
    try {
      const report = await financialSummaryApi.getReport(reportId, 'admin');
      setActiveReportId(report.id);
      setReportTitle(report.title || '');
      setReportPages(report.pages || []);
      setActiveThreadId(report.thread_id || null);
    } catch (e) {
      console.error('Failed to load report:', e);
    }
  }, []);

  // Delete a saved report
  const handleDeleteReport = useCallback(async (reportId) => {
    try {
      await financialSummaryApi.deleteReport(reportId, 'admin');
      if (activeReportId === reportId) {
        setActiveReportId(null);
        setReportPages([]);
        setReportTitle('');
      }
      await refreshReports();
    } catch (e) {
      console.error('Failed to delete report:', e);
    }
  }, [activeReportId, refreshReports]);

  // Update pages (from inline editing)
  const handlePageChange = useCallback((updatedPages) => {
    setReportPages(updatedPages);
  }, []);

  return (
    <div className="flex h-full bg-white">
      <div className="flex-shrink-0 flex flex-col report-chat-sidebar">
        <ReportChat
          threadId={activeThreadId}
          selectedCompanies={selectedCompanies}
          onSelectCompany={addCompany}
          onRemoveCompany={removeCompany}
          onThreadCreated={setActiveThreadId}
          onNewChat={handleNewChat}
          onReportPage={handleReportPage}
          onReportComplete={handleReportComplete}
        />
      </div>
      <ReportWorkspace
        reportTitle={reportTitle}
        pages={reportPages}
        onPageChange={handlePageChange}
        onSave={handleSave}
        saving={saving}
        activeReportId={activeReportId}
        savedReports={savedReports}
        onLoadReport={handleLoadReport}
        onDeleteReport={handleDeleteReport}
      />
    </div>
  );
}
